import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Space, Table, Tabs, Tooltip, Typography, message } from "antd";
import {
    GetV2Automation,
    GetV2AutomationRuns,
    GetV2AutomationSteps,
    GetV2RunLogs,
    V2RunListItemDto,
} from "../../api/v2/automations";
import { TestRecentActivities, TestTrackActivity, TestWorkerTick } from "../../api/v2/automationTest";
import { GetV2AutomationTriggers } from "../../api/v2/triggers";
import { GetConditionTreeBundle } from "../../api/v2/conditionTree";
import { ListV2Templates, V2TemplateListItemDto } from "../../api/v2/templates";
import AutomationTreeEditor from "./AutomationTreeEditor";
import useFlag from "../../hooks/useFlag";
import V2Tag from "../../components/v2/V2Tag";

const { Text } = Typography;

type Props = {
    open: boolean;
    domain: string;
    automationId: number | null;
    onClose: () => void;
};

type TrackPayload = {
    automation_id?: number | null;
    client_id: number;
    event_name: string;
    entity_type?: string | null;
    entity_id?: number | null;
    meta?: any;
};

export default function AutomationTestLabModal({ open, domain, automationId: initialAutomationId, onClose }: Props) {
    const { isFlagOn } = useFlag();
    const showInnerTools = isFlagOn("automation.lab.inner_tools");
    const LAB_CLIENT_ID_KEY = "v2_test_lab_client_id";
    const [setupForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [runs, setRuns] = useState<V2RunListItemDto[]>([]);
    const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
    const [logs, setLogs] = useState<any[]>([]);
    const [recentTracks, setRecentTracks] = useState<any[]>([]);
    const [lastTrackResponse, setLastTrackResponse] = useState<any>(null);
    const [playbookLines, setPlaybookLines] = useState<string[]>([]);
    const [entrySummary, setEntrySummary] = useState<string>("No entry condition loaded.");
    const [triggerSummary, setTriggerSummary] = useState<string>("No trigger loaded.");
    const [activeTab, setActiveTab] = useState<string>("playbook");
    const [requiredTrackEvents, setRequiredTrackEvents] = useState<string[]>([]);
    const [ready, setReady] = useState(false);
    const [selectedAutomationId, setSelectedAutomationId] = useState<number>(0);
    const [selectedClientId, setSelectedClientId] = useState<number>(0);
    const [defaultEntityType, setDefaultEntityType] = useState<string>("");
    const [defaultEntityId, setDefaultEntityId] = useState<number | undefined>(undefined);
    const [defaultMetaJson, setDefaultMetaJson] = useState<string>('{\n  "context": {\n    "room_name": "Suite"\n  }\n}');
    const [customEventName, setCustomEventName] = useState<string>("");
    const [autoTickEnabled, setAutoTickEnabled] = useState(false);
    const [editingClientId, setEditingClientId] = useState(false);
    const [clientIdDraft, setClientIdDraft] = useState<number>(0);
    const automationId = Number(selectedAutomationId || 0);
    const AUTO_TICK_SECONDS = 10;

    const formatLabDate = (v?: string | null) => {
        if (!v) return "-";
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return String(v);
        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
        }).format(d);
    };

    const formatLabDateTime = (v?: string | null) => {
        if (!v) return "-";
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return String(v);
        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }).format(d);
    };

    const collectEventNamesFromConditionTreeJson = (tree: any): string[] => {
        const out = new Set<string>();
        const groups = Array.isArray(tree?.groups) ? tree.groups : [];
        for (const g of groups) {
            const rules = Array.isArray(g?.rules) ? g.rules : [];
            for (const r of rules) {
                if (String(r?.field || "") !== "event_name") continue;
                const operator = String(r?.operator || "equals");
                const value = String(r?.value || "").trim();
                if (!value) continue;
                if (operator === "in") {
                    value
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean)
                        .forEach((x) => out.add(x));
                } else {
                    out.add(value);
                }
            }
        }
        return Array.from(out);
    };

    const parseJsonMaybe = (v: any) => {
        if (v == null) return null;
        if (typeof v === "string") {
            try { return JSON.parse(v); } catch { return null; }
        }
        return v;
    };

    const setDeep = (obj: any, path: string, value: any) => {
        const parts = path.split(".").filter(Boolean);
        if (!parts.length) return;
        let cur = obj;
        for (let i = 0; i < parts.length - 1; i++) {
            const p = parts[i];
            if (cur[p] == null || typeof cur[p] !== "object") cur[p] = {};
            cur = cur[p];
        }
        const last = parts[parts.length - 1];
        if (cur[last] == null) cur[last] = value;
    };

    const suggestContextValue = (name: string) => {
        const k = String(name || "").toLowerCase();
        if (k.includes("image")) return "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85";
        if (k.includes("url") || k.includes("link")) return "https://vuelve.co";
        if (k.includes("name")) return "Suite";
        if (k.includes("price")) return "1200";
        if (k.includes("email")) return "guest@example.com";
        if (k.includes("phone")) return "+14155552671";
        return "";
    };

    const buildDefaultMetaFromTemplates = (steps: any[], templatesMap: Record<number, V2TemplateListItemDto>) => {
        const context: any = {};
        const supportedServerPlaceholders = new Set<string>([
            "client.first_name",
            "client.last_name",
            "client.full_name",
            "client.email",
            "client.phone",
            "shop.name",
        ]);
        const templateIds = new Set<number>();
        (steps || []).forEach((s: any) => {
            const type = String(s?.step_type || "");
            const cfg = s?.config || {};
            if (type === "send_whatsapp_template") {
                const tId = Number(cfg?.template_id || 0);
                if (tId) templateIds.add(tId);
                return;
            }
            if (type === "action") {
                const key = String(cfg?.action_type || cfg?.action_key || "").trim();
                if (key === "send_whatsapp_template") {
                    const tId = Number(cfg?.template_id || 0);
                    if (tId) templateIds.add(tId);
                }
            }
        });

        templateIds.forEach((tId) => {
            const tpl = templatesMap[tId];
            if (!tpl) return;
            const schema = parseJsonMaybe(tpl.variables_schema_json);
            const vars = Array.isArray(schema) ? schema : [];
            vars.forEach((path: any) => {
                const p = String(path || "").trim();

                if (!p || supportedServerPlaceholders.has(p)) return;

                const imgArr = p.match(/^context\.images_urls\.(\d+)$/);
                if (imgArr) {
                    const idx = Number(imgArr[1]);
                    if (!Array.isArray(context.images_urls)) context.images_urls = [];
                    if (!context.images_urls[idx]) context.images_urls[idx] = suggestContextValue("image_url");
                    return;
                }

                const btnUrl = p.match(/^context\.buttons\.(\d+)\.url$/);
                if (btnUrl) {
                    const idx = Number(btnUrl[1]);
                    if (!Array.isArray(context.buttons)) context.buttons = [];
                    if (!context.buttons[idx]) context.buttons[idx] = { url: "", label: "View" };
                    if (!context.buttons[idx].url) context.buttons[idx].url = suggestContextValue("url");
                    if (!context.buttons[idx].label) context.buttons[idx].label = "View";
                    return;
                }

                const key = p.startsWith("context.") ? p.replace(/^context\./, "") : p;
                setDeep(context, key, suggestContextValue(key));
            });
        });

        if (!Object.keys(context).length) {
            context.room_name = "Suite";
        }

        return { context };
    };

    const loadPlaybook = async (nextAutomationId: number) => {
        if (!nextAutomationId) return;
        const [triggersRes, graphRes, templatesRes] = await Promise.all([
            GetV2AutomationTriggers(domain, nextAutomationId),
            GetV2AutomationSteps(domain, nextAutomationId, "published"),
            ListV2Templates(domain),
        ]);

        const lines: string[] = [];
        let entryText = "No entry condition loaded.";

        if (triggersRes.ok) {
            const trigger = (triggersRes.data || [])[0];
            if (trigger) {
                const cooldown = Number((trigger as any).cooldown_seconds || 0);
                const maxActive = Number((trigger as any).max_active_runs_per_client || 1);
                const triggerActive = Number((trigger as any).active || 0) === 1 ? "active" : "inactive";
                setTriggerSummary(`Trigger #${trigger.id} (${triggerActive}) · cooldown=${cooldown}s · max_active_runs_per_client=${maxActive}`);
                entryText = `Primary entry event: ${String(trigger.entry_event_name || "not_set")}`;
                if (trigger.entry_condition_id) {
                    const entryBundleRes = await GetConditionTreeBundle(domain, Number(trigger.entry_condition_id));
                    if (entryBundleRes.ok && entryBundleRes.data) {
                        const bundle = entryBundleRes.data;
                        const rootId = Number(bundle.root_condition_id || 0);
                        const rootItems = Array.isArray(bundle.itemsByConditionId?.[String(rootId)])
                            ? bundle.itemsByConditionId[String(rootId)]
                            : [];
                        const bits: string[] = [];
                        for (const item of rootItems) {
                            if (String(item.item_type) !== "basic_condition") continue;
                            const b = bundle.basicById?.[String(item.item_id)];
                            if (!b) continue;
                            const eventName = String(b.event_name || "").trim();
                            const aggregation = String(b.aggregation || "exists");
                            const operator = String(b.operator || "EXISTS");
                            const threshold = b.threshold != null ? String(b.threshold) : "";
                            bits.push(
                                aggregation === "count"
                                    ? `${eventName} count ${operator} ${threshold || "?"}`
                                    : `${eventName} ${operator}`
                            );
                        }
                        if (bits.length) entryText = `Entry condition: ${bits.join(" AND ")}`;
                    }
                }
            } else {
                setTriggerSummary("No trigger found for selected automation.");
            }
        } else {
            setTriggerSummary("Failed loading trigger summary.");
        }

        const eventNames = new Set<string>();
        if (triggersRes.ok) {
            for (const t of triggersRes.data || []) {
                const ev = String((t as any).entry_event_name || "").trim();
                if (ev) eventNames.add(ev);
            }
        }

        if (graphRes.ok) {
            for (const s of graphRes.data?.steps || []) {
                const type = String(s.step_type || "");
                const cfg = s.config || {};
                if (type === "wait_event") {
                    const ev = String(cfg.event_name || "").trim();
                    if (ev) eventNames.add(ev);
                }
                if (type === "condition") {
                    const fromCond = collectEventNamesFromConditionTreeJson(cfg.condition_tree_json || cfg.condition_tree || null);
                    fromCond.forEach((ev) => eventNames.add(ev));
                }
            }
        }

        if (templatesRes.ok) {
            const templates = Array.isArray(templatesRes.data?.templates) ? (templatesRes.data?.templates || []) : [];
            const byId: Record<number, V2TemplateListItemDto> = {};
            templates.forEach((t: any) => {
                const id = Number(t?.id || 0);
                if (id) byId[id] = t;
            });
            if (graphRes.ok) {
                const steps = Array.isArray(graphRes.data?.steps) ? graphRes.data.steps : [];
                const metaObj = buildDefaultMetaFromTemplates(steps, byId);
                setDefaultMetaJson(JSON.stringify(metaObj, null, 2));
            }
        }

        lines.push("Recommended test sequence:");
        let i = 1;
        for (const ev of Array.from(eventNames)) {
            lines.push(`${i}. Fire track event "${ev}"`);
            i += 1;
        }
        if (i === 1) lines.push("1. No event dependencies found in this automation.");
        lines.push(`${i}. Run worker tick`);
        lines.push(`${i + 1}. Watch run logs and branch transitions`);

        setEntrySummary(entryText);
        setPlaybookLines(lines);
        setRequiredTrackEvents(Array.from(eventNames));
    };

    const fetchRuns = async () => {
        if (!automationId) return;
        const res = await GetV2AutomationRuns(domain, automationId, 30);
        if (!res.ok) {
            message.error(res.error || "Failed loading runs");
            return;
        }
        const nextRuns = res.data?.runs || [];
        setRuns(nextRuns);
        if (!selectedRunId && nextRuns[0]?.id) setSelectedRunId(Number(nextRuns[0].id));
    };

    const fetchLogs = async (runId: number) => {
        const res = await GetV2RunLogs(domain, runId, 400);
        if (!res.ok) {
            message.error(res.error || "Failed loading run logs");
            return;
        }
        setLogs(res.data?.logs || []);
    };

    const fetchRecentTracks = async () => {
        const res = await TestRecentActivities(domain, 10);
        if (!res.ok) {
            message.error(res.error || "Failed loading tracks");
            return;
        }
        const rows = Array.isArray(res.data?.activities) ? res.data.activities : [];
        setRecentTracks(rows.slice().sort((a: any, b: any) => Number(b.id || 0) - Number(a.id || 0)));
    };

    const triggerTrack = async (payload: TrackPayload) => {
        setLoading(true);
        try {
            const res = await TestTrackActivity(domain, {
                automation_id: Number(payload.automation_id || 0) || null,
                client_id: Number(payload.client_id),
                event_name: String(payload.event_name || "").trim(),
                entity_type: payload.entity_type ? String(payload.entity_type).trim() : null,
                entity_id: payload.entity_id != null && String(payload.entity_id).trim() !== "" ? Number(payload.entity_id) : null,
                meta: payload.meta || null,
            });
            if (!res.ok) {
                message.error(res.error || "Track call failed");
                setLastTrackResponse({ ok: false, error: res.error || "Track call failed" });
                return;
            }
            setLastTrackResponse({ ok: true, data: res.data });
            message.success(`Track fired: ${payload.event_name}`);
            await fetchRecentTracks();
        } finally {
            setLoading(false);
        }
    };

    const triggerWorkerTick = async () => {
        setLoading(true);
        try {
            const res = await TestWorkerTick(domain);
            if (!res.ok) {
                message.error(res.error || "Worker tick failed");
                return;
            }
            message.success(`Worker tick processed ${Number(res.data?.processed || 0)} run(s)`);
            await fetchRecentTracks();
            await fetchRuns();
            if (selectedRunId) await fetchLogs(selectedRunId);
        } finally {
            setLoading(false);
        }
    };

    const parseMetaJsonText = (raw: string): any | null => {
        const text = String(raw || "").trim();
        if (!text) return null;
        try {
            return JSON.parse(text);
        } catch {
            throw new Error("Meta JSON must be valid JSON");
        }
    };

    const fireRequiredTrack = async (eventName: string) => {
        try {
            const meta = parseMetaJsonText(defaultMetaJson);
            await triggerTrack({
                automation_id: automationId || null,
                client_id: selectedClientId,
                event_name: eventName,
                entity_type: defaultEntityType || null,
                entity_id: defaultEntityId ?? null,
                meta,
            });
        } catch (e: any) {
            message.error(e?.message || "Failed to fire track");
        }
    };

    const fireCustomTrack = async () => {
        const eventName = String(customEventName || "").trim();
        if (!eventName) {
            message.error("Enter an event name for custom track");
            return;
        }
        await fireRequiredTrack(eventName);
    };

    const exportDebugPackage = async () => {
        const aid = Number(automationId || 0);
        if (!aid) {
            message.error("Select automation first");
            return;
        }
        setLoading(true);
        try {
            const [automationRes, triggersRes, liveGraphRes, publishedGraphRes] = await Promise.all([
                GetV2Automation(domain, aid),
                GetV2AutomationTriggers(domain, aid),
                GetV2AutomationSteps(domain, aid, "live"),
                GetV2AutomationSteps(domain, aid, "published"),
            ]);

            const payload = {
                exported_at: new Date().toISOString(),
                domain,
                selected_inputs: {
                    automation_id: automationId,
                    client_id: selectedClientId,
                    entity_type: defaultEntityType || null,
                    entity_id: defaultEntityId ?? null,
                    meta_json: defaultMetaJson,
                },
                selected_run_id: selectedRunId,
                automation: automationRes.ok ? automationRes.data : { error: automationRes.error || "failed" },
                triggers: triggersRes.ok ? triggersRes.data : { error: triggersRes.error || "failed" },
                graph_live: liveGraphRes.ok ? liveGraphRes.data : { error: liveGraphRes.error || "failed" },
                graph_published: publishedGraphRes.ok ? publishedGraphRes.data : { error: publishedGraphRes.error || "failed" },
                recent_tracks: recentTracks,
                runs: runs,
                run_logs: logs,
                last_track_response: lastTrackResponse,
            };

            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `automation-debug-${aid}-${Date.now()}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            message.success("Debug package exported");
        } finally {
            setLoading(false);
        }
    };

    const runConditionChecks = async () => {
        try {
            if (!automationId || !selectedClientId) {
                message.error("Select automation and client first");
                return;
            }
            setLoading(true);
            const res = await TestTrackActivity(domain, {
                automation_id: Number(automationId || 0) || null,
                only_condition_checks: true,
                client_id: Number(selectedClientId),
                event_name: "__condition_check__",
                entity_type: defaultEntityType ? String(defaultEntityType).trim() : null,
                entity_id: defaultEntityId != null && String(defaultEntityId).trim() !== "" ? Number(defaultEntityId) : null,
                meta: null,
            });
            if (!res.ok) {
                message.error(res.error || "Condition checks failed");
                setLastTrackResponse({ ok: false, error: res.error || "Condition checks failed" });
                return;
            }
            setLastTrackResponse({ ok: true, data: res.data });
            message.success("Condition checks completed");
        } catch (e: any) {
            message.error(e?.message || "Condition checks failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        const storedClientIdRaw = localStorage.getItem(LAB_CLIENT_ID_KEY) || "";
        const storedClientId = Number(storedClientIdRaw || 0);
        const hasStoredClientId = Number.isFinite(storedClientId) && storedClientId > 0;

        setReady(false);
        setSelectedRunId(null);
        setRuns([]);
        setLogs([]);
        setLastTrackResponse(null);
        setRequiredTrackEvents([]);
        setSelectedAutomationId(0);
        setSelectedClientId(0);
        setDefaultEntityType("");
        setDefaultEntityId(undefined);
        setDefaultMetaJson('{\n  "context": {\n    "room_name": "Suite"\n  }\n}');
        setCustomEventName("");
        setAutoTickEnabled(false);
        setEntrySummary("No entry condition loaded.");
        setTriggerSummary("No trigger loaded.");
        setPlaybookLines([]);
        setSelectedAutomationId(Number(initialAutomationId || 0));
        setupForm.setFieldsValue({
            client_id: hasStoredClientId ? storedClientId : undefined,
        });
        setSelectedClientId(hasStoredClientId ? storedClientId : 0);
        setClientIdDraft(hasStoredClientId ? storedClientId : 0);
        setEditingClientId(false);
        if (Number(initialAutomationId || 0) > 0 && hasStoredClientId) {
            setReady(true);
        }
        fetchRecentTracks().catch(() => null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialAutomationId]);

    useEffect(() => {
        if (!open || !ready || !automationId) return;
        loadPlaybook(automationId).catch(() => null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ready, automationId, domain]);

    useEffect(() => {
        if (!open || !ready || !polling || !automationId) return;
        const id = window.setInterval(async () => {
            await fetchRuns();
            if (selectedRunId) await fetchLogs(selectedRunId);
            await fetchRecentTracks();
        }, 2500);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ready, polling, automationId, selectedRunId, domain]);

    useEffect(() => {
        if (!open || !ready || !automationId || !autoTickEnabled) return;
        const id = window.setInterval(async () => {
            const res = await TestWorkerTick(domain);
            if (!res.ok) return;
            await fetchRuns();
            if (selectedRunId) await fetchLogs(selectedRunId);
            await fetchRecentTracks();
        }, AUTO_TICK_SECONDS * 1000);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ready, automationId, autoTickEnabled, selectedRunId, domain]);

    useEffect(() => {
        if (!open || !selectedRunId) return;
        fetchLogs(selectedRunId).catch(() => null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedRunId]);

    const runRows = useMemo(
        () => (runs || []).map((r) => ({ ...r, key: String(r.id) })),
        [runs]
    );

    const runRowsForClient = useMemo(() => {
        const cid = Number(selectedClientId || 0);
        if (!cid) return runRows;
        return runRows.filter((r: any) => Number(r.client_id || 0) === cid);
    }, [runRows, selectedClientId]);

    const selectedRun = useMemo(() => {
        const activeForClient = runRowsForClient.find((r: any) => String(r.status || "").toLowerCase() === "active");
        if (activeForClient) return activeForClient as any;
        const bySelected = runRowsForClient.find((r: any) => Number(r.id) === Number(selectedRunId || 0))
            || runRows.find((r: any) => Number(r.id) === Number(selectedRunId || 0));
        if (bySelected) return bySelected as any;
        return runRowsForClient[0] as any;
    }, [runRows, runRowsForClient, selectedRunId]);

    const parseMeta = (raw: any) => {
        if (!raw) return null;
        if (typeof raw === "object") return raw;
        if (typeof raw !== "string") return null;
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    };

    const mapStatusMessage = useMemo(() => {
        if (!automationId) return "Select an automation to render the run map.";
        if (!selectedClientId) return "Select a client ID to highlight a run.";
        if (!runRowsForClient.length) return "No run found for this automation + client combination yet.";
        return "";
    }, [automationId, selectedClientId, runRowsForClient.length]);


    const contextSourceRows = useMemo(() => {
        const rows = (logs || []).filter((l: any) => String(l?.event_type || "") === "context_resolved");
        return rows.map((l: any) => {
            const p = l?.payload_json || {};
            const source = p?.source || {};
            return {
                key: String(l?.id || Math.random()),
                step_index: l?.step_index,
                mode: String(p?.mode || ""),
                source_event_name: source?.event_name || "",
                source_event_ts: source?.event_ts || "",
                source_activity_id: source?.activity_id || "",
                scope: p?.query_scope || {},
            };
        });
    }, [logs]);

    const runHistoryByStep = useMemo(() => {
        const out: Record<number, { ranAt?: string; done?: boolean; doneAt?: string }> = {};
        for (const log of logs || []) {
            const rawStepIndex = log?.step_index;
            if (rawStepIndex == null || rawStepIndex === "") continue;
            const stepIndex = Number(rawStepIndex);
            if (!Number.isFinite(stepIndex) || stepIndex < 0) continue;
            const eventType = String(log?.event_type || "");
            const ts = String(log?.ts || log?.created_at || log?.at || "");
            if (!out[stepIndex]) out[stepIndex] = {};
            const current = out[stepIndex];
            if (ts) {
                const currentRanTs = current.ranAt ? new Date(current.ranAt).getTime() : 0;
                const nextTs = new Date(ts).getTime();
                if (!current.ranAt || (nextTs > 0 && (currentRanTs <= 0 || nextTs < currentRanTs))) {
                    current.ranAt = ts;
                }
            }
            if (eventType === "step_completed" || eventType === "step_failed" || eventType === "step_skipped") {
                current.done = true;
                if (ts) {
                    const currentDoneTs = current.doneAt ? new Date(current.doneAt).getTime() : 0;
                    const nextTs = new Date(ts).getTime();
                    if (!current.doneAt || (nextTs > 0 && nextTs > currentDoneTs)) {
                        current.doneAt = ts;
                    }
                }
            }
        }
        return out;
    }, [logs]);

    const runBranchSelectionByStep = useMemo(() => {
        const out: Record<number, { chosenNextStepIndexes?: number[]; transitionKey?: string; _ts?: string }> = {};
        for (const log of logs || []) {
            const rawStepIndex = log?.step_index;
            if (rawStepIndex == null || rawStepIndex === "") continue;
            const stepIndex = Number(rawStepIndex);
            if (!Number.isFinite(stepIndex) || stepIndex < 0) continue;
            const eventType = String(log?.event_type || "");
            if (eventType !== "step_completed") continue;
            const payload = log?.payload_json || {};
            const ts = String(log?.ts || log?.created_at || log?.at || "");
            const prev = out[stepIndex];
            if (prev && prev._ts && ts) {
                const prevTs = new Date(prev._ts).getTime();
                const nextTs = new Date(ts).getTime();
                if (prevTs > 0 && nextTs > 0 && nextTs < prevTs) continue;
            }
            const chosenNext = Array.isArray(payload?.next_step_indexes)
                ? payload.next_step_indexes
                    .map((x: any) => Number(x || 0))
                    .filter((x: number) => Number.isFinite(x) && x > 0)
                : [];
            out[stepIndex] = {
                chosenNextStepIndexes: chosenNext,
                transitionKey: String(payload?.transition_key || ""),
                _ts: ts,
            };
        }
        const cleaned: Record<number, { chosenNextStepIndexes?: number[]; transitionKey?: string }> = {};
        Object.entries(out).forEach(([k, v]) => {
            cleaned[Number(k)] = {
                chosenNextStepIndexes: v.chosenNextStepIndexes || [],
                transitionKey: v.transitionKey || "",
            };
        });
        return cleaned;
    }, [logs]);

    useEffect(() => {
        if (!open || !ready || !automationId) return;
        setSelectedRunId(null);
        fetchRuns().catch(() => null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ready, automationId, domain]);

    useEffect(() => {
        if (showInnerTools) return;
        if (activeTab === "checks" || activeTab === "raw") {
            setActiveTab("playbook");
        }
    }, [showInnerTools, activeTab]);

    useEffect(() => {
        if (!open) return;
        const activeForClient = runRowsForClient.find((r: any) => String(r.status || "").toLowerCase() === "active");
        if (activeForClient) {
            const nextId = Number((activeForClient as any).id || 0);
            if (nextId && Number(selectedRunId || 0) !== nextId) {
                setSelectedRunId(nextId);
            }
        }
    }, [open, runRowsForClient, selectedRunId]);

    const startLabSession = async () => {
        const v = await setupForm.validateFields(["client_id"]);
        const aid = Number(initialAutomationId || 0);
        const cid = Number(v.client_id || 0);
        if (!aid || !cid) return;
        setLoading(true);
        try {
            setSelectedAutomationId(aid);
            setSelectedClientId(cid);
            localStorage.setItem(LAB_CLIENT_ID_KEY, String(cid));
            setReady(true);
            setActiveTab("playbook");
            await loadPlaybook(aid);
            await fetchRecentTracks();
            await fetchRuns();
        } finally {
            setLoading(false);
        }
    };

    const applyClientIdFromHeader = async () => {
        const nextId = Number(clientIdDraft || 0);
        if (!nextId) {
            message.error("Client ID is required");
            return;
        }
        localStorage.setItem(LAB_CLIENT_ID_KEY, String(nextId));
        setSelectedClientId(nextId);
        setEditingClientId(false);
        setSelectedRunId(null);
        setLogs([]);
        await fetchRuns();
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1200}
            title="Automation Test Lab"
            wrapClassName="v2-lab-modal"
            zIndex={15000}
        >
            <style>{`
                .v2-lab-modal .ant-modal-content,
                .v2-lab-modal .ant-modal-content * {
                    font-family: 'quicksand-medium';
                }
                .v2-lab-modal .ant-btn,
                .v2-lab-modal .ant-tag,
                .v2-lab-modal .ant-table-thead > tr > th,
                .v2-lab-modal .ant-typography strong,
                .v2-lab-modal b {
                    font-family: 'quicksand-bold';
                    font-weight: 400;
                }
                .v2-lab-run-row-selected > td {
                    background: rgba(41, 103, 255, 0.08) !important;
                }
            `}</style>
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
                {!ready ? (
                    <Space direction="vertical" style={{ width: "100%" }} size={16}>
                        <Text type="secondary">Step 1: choose client</Text>
                        <Form form={setupForm} layout="inline" style={{ rowGap: 8 }}>
                            <Form.Item name="client_id" label="Client ID" rules={[{ required: true }]}>
                                <InputNumber min={1} />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" loading={loading} onClick={startLabSession}>
                                    Continue
                                </Button>
                            </Form.Item>
                        </Form>
                    </Space>
                ) : (
                    <>
                        <Space wrap>
                            <V2Tag color="blue">Automation #{automationId}</V2Tag>
                            {!editingClientId ? (
                                <V2Tag color="green" style={{ cursor: "pointer" }} onClick={() => {
                                    setClientIdDraft(Number(selectedClientId || 0));
                                    setEditingClientId(true);
                                }}>
                                    Client #{selectedClientId}
                                </V2Tag>
                            ) : (
                                <Space size={6}>
                                    <InputNumber
                                        min={1}
                                        value={clientIdDraft || undefined}
                                        onChange={(v) => setClientIdDraft(Number(v || 0))}
                                        style={{ width: 120 }}
                                    />
                                    <Button size="small" type="primary" onClick={applyClientIdFromHeader}>Save</Button>
                                    <Button size="small" onClick={() => setEditingClientId(false)}>Cancel</Button>
                                </Space>
                            )}
                            {showInnerTools ? (
                                <Button loading={loading} onClick={exportDebugPackage}>Export debug package</Button>
                            ) : null}
                            <Button onClick={async () => {
                                await fetchRecentTracks();
                                await fetchRuns();
                                if (selectedRunId) await fetchLogs(selectedRunId);
                            }}>
                                Refresh all
                            </Button>
                        </Space>

                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={[
                        {
                            key: "playbook",
                            label: "Playbook",
                            children: (
                                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                    <div style={{ background: "#f7f9ff", border: "1px solid #d7e3ff", borderRadius: 8, padding: 10 }}>
                                        <Text strong>Entry condition</Text>
                                        <div><Text>{entrySummary}</Text></div>
                                        <div style={{ marginTop: 4 }}><Text type="secondary">{triggerSummary}</Text></div>
                                        <div style={{ marginTop: 8 }}>
                                            <Text strong>Playbook</Text>
                                            <ul style={{ margin: "8px 0 0 18px" }}>
                                                {playbookLines.map((line, idx) => (
                                                    <li key={`${idx}_${line}`}><Text>{line}</Text></li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </Space>
                            ),
                        },
                        {
                            key: "tracks",
                            label: "Tracks",
                            children: (
                                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                    <div style={{ display: "flex", gap: 12, alignItems: "stretch", width: "100%" }}>
                                        <div style={{ background: "#f7f9ff", border: "1px solid #d7e3ff", borderRadius: 8, padding: 10, width: "50%" }}>
                                            <Space wrap>
                                                <Input
                                                    placeholder="default entity type"
                                                    value={defaultEntityType}
                                                    onChange={(e) => setDefaultEntityType(e.target.value)}
                                                    style={{ width: 180 }}
                                                />
                                                <InputNumber
                                                    placeholder="default entity id"
                                                    value={defaultEntityId}
                                                    onChange={(v) => setDefaultEntityId(v == null ? undefined : Number(v))}
                                                    min={1}
                                                    style={{ width: 180 }}
                                                />
                                            </Space>
                                            <div style={{ marginTop: 8 }}>
                                                <Input.TextArea
                                                    rows={7}
                                                    value={defaultMetaJson}
                                                    onChange={(e) => setDefaultMetaJson(e.target.value)}
                                                    placeholder="Default meta JSON used by quick track buttons"
                                                />
                                            </div>
                                        </div>
                                        <div style={{ width: "50%", display: "flex", flexDirection: "column", gap: 12 }}>
                                            <div style={{ background: "#f7f9ff", border: "1px solid #d7e3ff", borderRadius: 8, padding: 10 }}>
                                                <Text strong>Required tracking events</Text>
                                                <div style={{ marginTop: 8 }}>
                                                    <Space wrap>
                                                        {(requiredTrackEvents || []).length ? (
                                                            requiredTrackEvents.map((ev) => (
                                                                <Button key={ev} loading={loading} onClick={() => fireRequiredTrack(ev)}>
                                                                    Fire: {ev}
                                                                </Button>
                                                            ))
                                                        ) : (
                                                            <Text type="secondary">No required events were detected for this automation.</Text>
                                                        )}
                                                    </Space>
                                                </div>
                                            </div>
                                            <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 8, padding: 10 }}>
                                                <Space wrap>
                                                    <Input
                                                        placeholder="Custom event name"
                                                        value={customEventName}
                                                        onChange={(e) => setCustomEventName(e.target.value)}
                                                        style={{ width: 220 }}
                                                    />
                                                    <Button loading={loading} onClick={fireCustomTrack}>Fire custom track</Button>
                                                </Space>
                                            </div>
                                        </div>
                                    </div>
                                    <Space>
                                        <Button onClick={fetchRecentTracks}>Refresh tracks</Button>
                                    </Space>
                                    <Table
                                        size="small"
                                        dataSource={(recentTracks || []).map((r: any) => ({ ...r, key: String(r.id) }))}
                                        pagination={false}
                                        columns={[
                                            { title: "ID", dataIndex: "id", width: 80 },
                                            {
                                                title: "At",
                                                dataIndex: "event_ts",
                                                width: 180,
                                                render: (v: string) => (
                                                    <Tooltip title={formatLabDateTime(v)}>
                                                        <span>{formatLabDate(v)}</span>
                                                    </Tooltip>
                                                ),
                                            },
                                            { title: "Client", dataIndex: "client_id", width: 100 },
                                            { title: "Event", dataIndex: "event_name", width: 220 },
                                            { title: "Entity type", dataIndex: "entity_type", width: 130 },
                                            { title: "Entity id", dataIndex: "entity_id", width: 120 },
                                            {
                                                title: "Context",
                                                width: 280,
                                                render: (_: any, row: any) => {
                                                    const parsed = parseMeta(row?.meta);
                                                    const context = parsed?.context || null;
                                                    if (!context) return <Text type="secondary">-</Text>;
                                                    const compact = JSON.stringify(context);
                                                    return (
                                                        <Tooltip
                                                            title={
                                                                <pre style={{ margin: 0, whiteSpace: "pre-wrap", maxWidth: 560 }}>
                                                                    {JSON.stringify(context, null, 2)}
                                                                </pre>
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    maxWidth: 260,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    fontSize: 11,
                                                                    lineHeight: "18px",
                                                                    cursor: "default",
                                                                }}
                                                            >
                                                                {compact}
                                                            </div>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                            {
                                                title: "Meta",
                                                width: 220,
                                                render: (_: any, row: any) => {
                                                    const parsed = parseMeta(row?.meta);
                                                    if (!parsed) return <Text type="secondary">-</Text>;
                                                    const compact = JSON.stringify(parsed);
                                                    return (
                                                        <Tooltip
                                                            title={
                                                                <pre style={{ margin: 0, whiteSpace: "pre-wrap", maxWidth: 560 }}>
                                                                    {JSON.stringify(parsed, null, 2)}
                                                                </pre>
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    maxWidth: 200,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    fontSize: 11,
                                                                    lineHeight: "18px",
                                                                    cursor: "default",
                                                                }}
                                                            >
                                                                {compact}
                                                            </div>
                                                        </Tooltip>
                                                    );
                                                },
                                            },
                                        ]}
                                    />
                                </Space>
                            ),
                        },
                        {
                            key: "runs",
                            label: "Runs",
                            children: (
                                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                    <Space>
                                        <Button onClick={fetchRuns}>Refresh runs</Button>
                                        <Button loading={loading} onClick={triggerWorkerTick}>Run worker tick</Button>
                                        <Button onClick={() => setPolling((v) => !v)}>{polling ? "Stop polling" : "Start polling"}</Button>
                                        <Button onClick={() => setAutoTickEnabled((v) => !v)}>
                                            {autoTickEnabled ? `Stop auto tick (${AUTO_TICK_SECONDS}s)` : `Start auto tick (${AUTO_TICK_SECONDS}s)`}
                                        </Button>
                                    </Space>
                                    <Table
                                        size="small"
                                        dataSource={runRows}
                                        pagination={{ pageSize: 6 }}
                                        rowClassName={(r: any) =>
                                            Number(r?.id || 0) === Number(selectedRunId || 0) ? "v2-lab-run-row-selected" : ""
                                        }
                                        columns={[
                                            { title: "Run", dataIndex: "id", width: 90 },
                                            {
                                                title: "Status",
                                                dataIndex: "status",
                                                width: 120,
                                                render: (v: string) => {
                                                    const s = String(v || "").toLowerCase();
                                                    const color = s === "completed" ? "green" : s === "active" ? "blue" : s === "error" ? "red" : "default";
                                                    return <V2Tag color={color}>{v}</V2Tag>;
                                                },
                                            },
                                            { title: "Current step", dataIndex: "current_step_index", width: 130 },
                                            { title: "Entered", dataIndex: "entered_at", width: 220 },
                                            { title: "Ended", dataIndex: "ended_at", width: 220 },
                                            { title: "Reason", dataIndex: "cancel_reason" },
                                        ]}
                                        onRow={(r) => ({
                                            onClick: () => setSelectedRunId(Number((r as any).id)),
                                        })}
                                    />
                                    <div>
                                        <Text strong>Run map</Text>
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="secondary">
                                                Read-only flow map. Highlighted node = current step for selected run/client.
                                            </Text>
                                        </div>
                                        {mapStatusMessage ? (
                                            <div style={{ marginTop: 4 }}>
                                                <Text type="secondary">{mapStatusMessage}</Text>
                                            </div>
                                        ) : null}
                                        <div style={{ marginTop: 8 }}>
                                            <AutomationTreeEditor
                                                domain={domain}
                                                automationId={automationId}
                                                editable={false}
                                                stepsSource="published"
                                                highlightStepIndex={Number(selectedRun?.current_step_index || 0) || null}
                                                stageHeight={420}
                                                runHistoryByStep={runHistoryByStep}
                                                runBranchSelectionByStep={runBranchSelectionByStep}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        {showInnerTools ? (
                                            <>
                                                <Text strong>Run logs {selectedRunId ? `(#${selectedRunId})` : ""}</Text>
                                                <pre
                                                    style={{
                                                        marginTop: 8,
                                                        background: "#0f1115",
                                                        color: "#dce3f0",
                                                        borderRadius: 8,
                                                        padding: 12,
                                                        maxHeight: 320,
                                                        overflow: "auto",
                                                        fontSize: 12,
                                                    }}
                                                >
{JSON.stringify(logs, null, 2)}
                                                </pre>
                                            </>
                                        ) : null}
                                    </div>
                                    {showInnerTools ? (
                                        <div>
                                            <Text strong>Resolved context source</Text>
                                            <Table
                                                size="small"
                                                pagination={false}
                                                dataSource={contextSourceRows}
                                                columns={[
                                                    { title: "Step", dataIndex: "step_index", width: 80 },
                                                    { title: "Mode", dataIndex: "mode", width: 180 },
                                                    { title: "Source event", dataIndex: "source_event_name", width: 200 },
                                                    { title: "Event time", dataIndex: "source_event_ts", width: 220 },
                                                    { title: "Activity ID", dataIndex: "source_activity_id", width: 120 },
                                                    {
                                                        title: "Scope",
                                                        dataIndex: "scope",
                                                        render: (v: any) => (
                                                            <pre style={{ margin: 0, fontSize: 11, maxWidth: 280, whiteSpace: "pre-wrap" }}>
                                                                {JSON.stringify(v || {}, null, 2)}
                                                            </pre>
                                                        ),
                                                    },
                                                ]}
                                            />
                                        </div>
                                    ) : null}
                                </Space>
                            ),
                        },
                        ...(showInnerTools ? [{
                            key: "checks",
                            label: "Condition checks",
                            children: (
                                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                    <Space>
                                        <Button loading={loading} onClick={runConditionChecks}>Run condition checks</Button>
                                    </Space>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        dataSource={(() => {
                                            const cc = lastTrackResponse?.data?.conditionChecks || null;
                                            if (!cc) return [];
                                            const rows: any[] = [];
                                            for (const e of cc.entry_conditions || []) {
                                                rows.push({
                                                    key: `entry-${e.trigger_id}-${e.entry_condition_id}`,
                                                    scope: "Entry",
                                                    target: `Trigger #${e.trigger_id}`,
                                                    source: `Condition #${e.entry_condition_id}`,
                                                    condition_text: String(e.condition_text || ""),
                                                    matched: !!e.matched,
                                                    required: Array.isArray(e.required_inputs) ? e.required_inputs.join(", ") : "",
                                                    missing: Array.isArray(e.missing_inputs) ? e.missing_inputs.join(", ") : "",
                                                });
                                            }
                                            for (const c of cc.tree_conditions || []) {
                                                rows.push({
                                                    key: `tree-${c.step_index}-${c.source}`,
                                                    scope: "Tree",
                                                    target: `Step #${c.step_index}`,
                                                    source: c.source === "condition_id" ? `Condition #${c.condition_id}` : "condition_tree_json",
                                                    condition_text: String(c.condition_text || ""),
                                                    matched: !!c.matched,
                                                    required: Array.isArray(c.required_inputs) ? c.required_inputs.join(", ") : "",
                                                    missing: Array.isArray(c.missing_inputs) ? c.missing_inputs.join(", ") : "",
                                                    extra:
                                                        c.source === "condition_tree_json"
                                                            ? `scope=${c.scope_mode}, entity=${c.entity_scope_mode}, snapshots=${c.snapshots_count}`
                                                            : "",
                                                });
                                            }
                                            return rows;
                                        })()}
                                        columns={[
                                            { title: "Scope", dataIndex: "scope", width: 110 },
                                            { title: "Target", dataIndex: "target", width: 180 },
                                            { title: "Source", dataIndex: "source", width: 220 },
                                            { title: "Condition", dataIndex: "condition_text", width: 380 },
                                            {
                                                title: "Result",
                                                dataIndex: "matched",
                                                width: 120,
                                                render: (v: boolean) => <V2Tag color={v ? "green" : "red"}>{v ? "TRUE" : "FALSE"}</V2Tag>,
                                            },
                                            { title: "Required input", dataIndex: "required", width: 180 },
                                            { title: "Missing input", dataIndex: "missing", width: 180 },
                                            { title: "Details", dataIndex: "extra" },
                                        ]}
                                    />
                                </Space>
                            ),
                        }] : []),
                        ...(showInnerTools ? [{
                            key: "raw",
                            label: "Raw response",
                            children: (
                                <div>
                                    <Text strong>Last track call response</Text>
                                    <pre
                                        style={{
                                            marginTop: 8,
                                            background: "#0f1115",
                                            color: "#dce3f0",
                                            borderRadius: 8,
                                            padding: 12,
                                            maxHeight: 360,
                                            overflow: "auto",
                                            fontSize: 12,
                                        }}
                                    >
{JSON.stringify(lastTrackResponse, null, 2)}
                                    </pre>
                                </div>
                            ),
                        }] : []),
                            ]}
                        />
                    </>
                )}
            </Space>
        </Modal>
    );
}
